import {cloneDeep, extend, partial, isNil, isNumber} from 'lodash';
import moment from 'moment';

import {t} from 'i18n';

const numberPattern = /(\d+)(\d{3})/;
const numberFormat = [
  {
    symbol: 'e',
    value: 1e18,
  },
  {
    symbol: 'p',
    value: 1e15,
  },
  {
    symbol: 't',
    value: 1e12,
  },
  {
    symbol: 'b',
    value: 1e9,
  },
  {
    symbol: 'm',
    value: 1e6,
  },
  {
    symbol: 'k',
    value: 1e3,
  },
];

export const numericSymbols = numberFormat.reduceRight(
  (agg, entry) => (agg = agg.concat(entry.symbol)),
  []
);

function customSort(a, b, column, dir) {
  let ac = a.get(column),
    bc = b.get(column);

  let pat1 = /[^\-\d\.]/,
    pat2 = /-?[\d\.]+/,
    pat3 = /^[^.]+.[^.]+$/;

  if (dir === 'desc') {
    // We need to know if there aren't any non-number characters
    // and that there are numbers-only characters and maybe a dots
    // if we have a float.
    // Oh, also a '-' for negative numbers!
    if (
      !pat1.test(ac) &&
      pat2.test(ac) &&
      pat3.test(ac) &&
      !pat1.test(bc) &&
      pat2.test(bc) &&
      pat3.test(bc)
    ) {
      if (ac - 0 < bc - 0) {
        return 1;
      }
      if (ac - 0 > bc - 0) {
        return -1;
      }
    } else {
      if (ac === null) {
        ac = '';
      }
      if (bc === null) {
        bc = '';
      }

      if (ac < bc) {
        return 1;
      }
      if (ac > bc) {
        return -1;
      }
    }
  } else if (
    !pat1.test(ac) &&
    pat2.test(ac) &&
    pat3.test(ac) &&
    !pat1.test(bc) &&
    pat2.test(bc) &&
    pat3.test(bc)
  ) {
    if (ac - 0 < bc - 0) {
      return -1;
    }
    if (ac - 0 > bc - 0) {
      return 1;
    }
  } else {
    if (ac === null) {
      ac = '';
    }
    if (bc === null) {
      bc = '';
    }
    if (ac < bc) {
      return -1;
    }
    if (ac > bc) {
      return 1;
    }
  }
}

export const formatDateToISO8601Date = function (d) {
  function pad(n) {
    return n < 10 ? `0${n}` : n;
  }

  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
    d.getUTCDate()
  )}`;
};
export const severityFormatter = function (severity) {
  if (severity < 4) {
    return t('moderate');
  } else if (severity < 8) {
    return t('severe');
  }

  return t('critical');
};
export const dateDaysDifference = function (dateA, dateB = new Date()) {
  return moment(dateA).diff(dateB);
};
export const remainingTimeFormatter = function (milliseconds) {
  return new moment.duration(milliseconds).humanize();
};
export const remainingTimeFromNowFormatter = function (dateA, remainingTime) {
  if (dateA === null) {
    return t('ra.project.entity.neverExpire');
  }
  if (remainingTime === -1) {
    return t('Past Due');
  }

  return remainingTimeFormatter(dateDaysDifference(dateA));
};
export const dateFormatter = function (
  date,
  format = 'llll',
  isRemoveTime = true,
  displayAsUtc = false
) {
  if (!date) {
    return;
  }
  let momentDate = displayAsUtc ? moment(date).utc() : moment(date);

  let formattedDate = momentDate.format(format);

  if (isRemoveTime) {
    formattedDate = formattedDate.replace(/\s?[0-9]{1,2}:[0-9]{1,2}\s?.*/, '');
  }

  return formattedDate;
};
export const cvssFormatter = function (cvssScore) {
  return Math.round(cvssScore * 100) / 100;
};
export const digitFormat = function (number) {
  if (!isNumber(number)) {
    return;
  }

  let riskScore = parseFloat(number) || 0;

  let formatted = `${Math.round(riskScore)}`;

  // Adding comma after 3 digits.
  while (numberPattern.test(formatted)) {
    formatted = formatted.replace(numberPattern, '$1,$2');
  }

  return formatted;
};
export const formatRiskScore = function (rawRiskScore) {
  let riskScore = parseFloat(rawRiskScore) || 0;

  let formatted = '';

  if (riskScore < 99.5) {
    formatted = riskScore.toFixed(1);
  } else {
    formatted = digitFormat(riskScore, 2);
  }

  return formatted;
};
export const formatNumber = function (num, digits) {
  if (!isNumber(num)) {
    return;
  }

  let isNegative = false;

  if (num < 0) {
    isNegative = true;
  }

  for (let i = 0; i < numberFormat.length; i++) {
    if (num >= numberFormat[i].value) {
      return (
        Number((num / numberFormat[i].value).toFixed(digits)).toString() +
        numberFormat[i].symbol
      );
    }
  }

  if (num < 1e3) {
    return num.toFixed();
  }

  if (isNegative) {
    num = `-${num}`;
  }

  return num;
};

export const parseTime = function (time) {
  return new Date(
    time.year,
    time.monthValue - 1,
    time.dayOfMonth,
    time.hour,
    time.minute,
    time.second
  );
};

export const formatTime = function (time, format = 'l') {
  if (time) {
    const date = typeof time !== 'string' ? parseTime(time) : time;

    return dateFormatter(date, format);
  }
};

export const formatTimeLong = function (time) {
  if (!time) {
    return;
  }
  const date = parseTime(time);

  return dateFormatter(date, 'l');
};

export const percentage = function (numerator, denominator) {
  return denominator > 0 ? Math.round((numerator / denominator) * 100) : 100;
};

export const percentLabel = function (numerator, denominator) {
  return t('percent', {percent: percentage(numerator, denominator)});
};

export const percentGain = function (numerator, denominator, precision = 2) {
  const percent =
    denominator > 0
      ? ((numerator / denominator) * 100).toFixed(precision)
      : denominator === 0 && numerator === 0
      ? 0
      : 100;

  return t('percent', {percent});
};

/**
 * Calculates the delta (as a percentage) between @param current and @param initial. For example,
 * percentChange(3, 2) would return 50.00% (e.g. relative to 2 there's a 50% increase, from 2 to 3).
 *
 * @param current the current value used in the percentage change calculation
 * @param initial the initial value used in the percentage change calculation
 * @param precision how many decimal points to affix to the calculated percentage
 * @returns a string representation of the calculated value (e.g. 25.00%)
 */
export const percentChange = function (current, initial, precision = 2) {
  const percent = (initial > 0
    ? Math.abs((current / initial - 1) * 100)
    : 100
  ).toFixed(precision);

  return t('percent', {percent});
};

export const comparator = function (a, b) {
  if (this.sortColumn) {
    if (this.sortColumn === 'riskScoreDisplay') {
      return customSort(a, b, 'riskScore', this.sortDirection);
    } else if (this.sortColumn === 'dateDisplay') {
      return customSort(a, b, 'date', this.sortDirection);
    }

    return customSort(a, b, this.sortColumn, this.sortDirection);
  }
};

export const getColorForPercentage = function (pct) {
  let percentColors = [
    {
      color: {
        b: 0x6b,
        g: 0xe1,
        r: 0xfa,
      },
      pct: 0.0,
    },
    {
      color: {
        b: 0x4c,
        g: 0x4c,
        r: 0xe6,
      },
      pct: 1.0,
    },
  ];

  let i;

  for (i = 1; i < percentColors.length - 1; i++) {
    if (pct < percentColors[i].pct) {
      break;
    }
  }
  let lower = percentColors[i - 1];

  let upper = percentColors[i];

  let range = upper.pct - lower.pct;

  let rangePct = (pct - lower.pct) / range;

  let pctLower = 1 - rangePct;

  let pctUpper = rangePct;

  let color = {
    b: Math.floor(lower.color.b * pctLower + upper.color.b * pctUpper),
    g: Math.floor(lower.color.g * pctLower + upper.color.g * pctUpper),
    r: Math.floor(lower.color.r * pctLower + upper.color.r * pctUpper),
  };

  return `rgb(${[color.r, color.g, color.b].join(',')})`;
};

export const ellide = function (name, maxLength) {
  if (name && name.length > maxLength) {
    name = `${name.substring(0, maxLength - 4)}...`;
  }

  return name;
};

export const getPercentage = function (value, total, decimals) {
  if (total === 0) {
    return 0;
  }

  if (!decimals) {
    return Math.round((value / total) * 100);
  }

  return ((value / total) * 100).toFixed(decimals);
};

export const getFormattedPercentage = function (decimal) {
  const percentage = decimal * 100;

  let numberOfDecimals = 0;

  if (percentage < 1 && percentage > 0) {
    numberOfDecimals = 2;
  }

  return numberOfDecimals === 0
    ? Math.round(percentage)
    : percentage.toFixed(numberOfDecimals);
};

export const getFloorFormattedPercentage = function (decimal) {
  const percentage = decimal * 100;

  let numberOfDecimals = 0;

  if (percentage < 1 && percentage > 0) {
    numberOfDecimals = 2;
  }

  return numberOfDecimals === 0
    ? Math.floor(percentage)
    : percentage.toFixed(numberOfDecimals);
};

export const getTotalForProp = function (items, prop) {
  return items.reduce((a, b) => a + b[prop], 0);
};

export const getPeriodTime = function (days) {
  if (!days) {
    days = 0;
  }
  let date = new Date();

  date.setDate(date.getDate() + days);

  return date.getTime();
};

export const stringHashCode = function (str) {
  let hash = 0;

  if (str && str.length) {
    for (let i = 0; i < str.length; i++) {
      let ch = str.charCodeAt(i);

      hash = (hash << 5) - hash + ch;
      hash = hash & hash;
    }
  }

  return hash;
};

export const setCursorAtTextEnd = function (el) {
  if (document.createRange && window.getSelection) {
    let range = document.createRange(),
      selection = window.getSelection();

    range.selectNodeContents(el);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  return this;
};

export const setCursurAtTextboxEnd = function (el) {
  el.focus();

  if (el.setSelectionRange) {
    let len = el.value ? el.value.length : 0;

    el.setSelectionRange(len, len);
  }
};

export const updateDateField = function (element) {
  let expirationTime = element.expirationTime;

  if (expirationTime) {
    let dateValue = new Date(
      expirationTime.year,
      expirationTime.monthValue - 1,
      expirationTime.dayOfMonth,
      expirationTime.hour,
      expirationTime.minute,
      expirationTime.second
    );

    element.dateDisplay = this.formatDateToISO8601Date(dateValue);
    element.date = dateValue.getTime();
  } else {
    element.dateDisplay = '';
    element.date = 0;
  }
};

export const duplicateDashboard = function (current, attrs) {
  if (current) {
    let clone = cloneDeep(current);

    delete clone.uuid;

    return extend(clone, attrs);
  }
};

const rfc3986 = {
  '!': '%21',
  '#': '%23',
  "'": '%27',
  '(': '%28',
  ')': '%29',
  '*': '%2A',
};

export const encodeURIComponent = function (str) {
  return window
    .encodeURIComponent(str)
    .replace(/[!'()#*]/g, (match, position, str) => {
      const char = str[position];

      return rfc3986[char] || char;
    });
};

export const encodeParam = function (param) {
  return encodeURIComponent(param).replace(/%20/g, '+');
};

export const getIdFromKey = function (key) {
  if (key) {
    return key.split('-').pop();
  }
};

export const dasherize = function (str) {
  return str.replace(
    /[A-Z]/g,
    (char, index) => (index !== 0 ? '-' : '') + char.toLowerCase()
  );
};

export const getSummaryPercentage = function (value, total) {
  if (total === 0) {
    return 0;
  }

  let percent = (value / total) * 100;

  let percentage = percent < 1 && percent !== 0 ? percent : Math.round(percent);

  return this.formatNumber(percentage, 2);
};

export const deleteCookie = function (name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

// https://gist.github.com/tyler-johnson/cb366fddb64d35fc025a
export const transitionEndEventName = function () {
  let i,
    undefined,
    el = document.createElement('div'),
    transitions = {
      MozTransition: 'transitionend',
      OTransition: 'otransitionend', // oTransitionEnd in very old Opera
      WebkitTransition: 'webkitTransitionEnd',
      transition: 'transitionend',
    };

  for (i in transitions) {
    if (transitions.hasOwnProperty(i) && el.style[i] !== undefined) {
      return transitions[i];
    }
  }

  return 'transitionend';
};

export const isChrome = () => !isNil(window.chrome);

export const MAX_JAVA_INT = Math.pow(2, 31) - 1;

export const shortDateFormatter = partial(
  dateFormatter,
  partial.placeholder,
  'l'
);

export const hasCreatedDateAndEndDate = (data) => {
  const THIRTY_DAY_INTERVAL = 1000000;
  const _end = data?.createDate + THIRTY_DAY_INTERVAL;

  return data?.endDate ? data : {...data, endDate: _end};
};
